import React, { useState } from "react";
import { CategoryDto, DataMapDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import { DescriptionCheckbox } from "../../input/DescriptionCheckbox";
import { Close, SvgIconComponent } from "@mui/icons-material";
import { Text } from "../../../../common/components/input/Text";
import { Button, Chip } from "@mui/material";
import { Form } from "react-final-form";
import { Api } from "../../../../common/service/Api";
import { OrganizationPublicId } from "../../../../common/service/server/Types";
import { RefreshFn } from "../../../../common/hooks/api";
import _ from "lodash";
import { GroupIcon } from "../../../pages/get-compliant/data-map/GroupIcon";
import { ConfirmDialog } from "../../dialog";
import { DisableableButton } from "../../Disableable";

type CollectionCheckboxGroupProps = {
  categories: CategoryDto[];
  collectedIds: string[];
  pendingIds: string[];
  name?: string;
  groupKey?: string;
  helptext?: string;
  dataMap: DataMapDto;
  onChecked: (id: string, isCollected: boolean) => void;
  disabledCategoryIds?: string[];
  disabledTooltipFn?: (checked: boolean) => string;
  columns?: boolean;
  icon?: SvgIconComponent;
  allowCustom?: boolean;
  orgId: OrganizationPublicId;
  refreshDataMap: RefreshFn;
  autoselected?: string[];
  collectionGroupId?: string;
};
export const CollectionCheckboxGroup: React.FC<CollectionCheckboxGroupProps> = ({
  categories,
  collectedIds,
  pendingIds,
  name,
  onChecked,
  dataMap,
  groupKey,
  disabledCategoryIds,
  disabledTooltipFn,
  columns = true,
  helptext,
  allowCustom = false,
  orgId,
  refreshDataMap,
  autoselected,
  collectionGroupId,
}) => {
  // Check if any category in this group is sensitive in any region
  const hasSensitivePics = categories.some((cat) => cat.sensitiveRegions?.length > 0);

  return (
    <div className="collection-checkbox-group">
      <div className="collection-checkbox-group-header">
        {groupKey && (
          <div className="collection-checkbox-group-icon">
            <GroupIcon groupName={groupKey} color="primary" />
          </div>
        )}
        {name && <h5 className="collection-checkbox-group-title">{name}</h5>}
        {helptext && (
          <p
            className="collection-checkbox-group-helptext"
            dangerouslySetInnerHTML={{ __html: helptext }}
          />
        )}
      </div>
      <div className="collection-checkbox-group-body">
        <CollectionCheckboxes
          refreshDataMap={refreshDataMap}
          orgId={orgId}
          categories={categories}
          dataMap={dataMap}
          checked={collectedIds}
          onChecked={onChecked}
          disabledCategoryIds={disabledCategoryIds}
          disabledTooltipFn={disabledTooltipFn}
          pendingIds={pendingIds}
          columns={columns}
          allowCustom={allowCustom}
          autoselected={autoselected}
          collectionGroupId={collectionGroupId}
        />
        {hasSensitivePics && (
          <div
            className="mt-sm text-sm text-muted"
            style={{ color: "#d32f2f", fontWeight: "bold" }}
          >
            *Sensitive data in at least one US State
          </div>
        )}
      </div>
    </div>
  );
};

type CollectionCheckboxesProps = {
  orgId: OrganizationPublicId;
  categories: CategoryDto[];
  checked: string[];
  onChecked: (id: string, isCollected: boolean) => void;
  dataMap: DataMapDto;
  descriptions?: boolean;
  disabledCategoryIds?: string[];
  disabledTooltipFn?: (checked: boolean) => string;
  pendingIds: string[];
  autoselected?: string[];
  columns?: boolean;
  allowCustom?: boolean;
  refreshDataMap: RefreshFn;
  collectionGroupId?: string;
};
const CollectionCheckboxes: React.FC<CollectionCheckboxesProps> = ({
  orgId,
  categories,
  checked,
  onChecked,
  dataMap,
  descriptions = true,
  disabledCategoryIds = [],
  disabledTooltipFn,
  pendingIds,
  autoselected = [],
  columns = true,
  allowCustom = false,
  refreshDataMap,
  collectionGroupId,
}) => {
  const [adding, setAdding] = useState(false);
  const [customPicLoading, setCustomPicLoading] = useState(false);

  const fillDynamicDescriptionText = (
    cat: CategoryDto,
    collected: string[],
    dataMap: DataMapDto,
  ): CategoryDto => {
    const sensitiveCategories = _.sortBy(dataMap?.categories, ["picGroupOrder", "picGroupName"])
      .filter((cat) => cat.isSensitiveInCa && collected.includes(cat.id))
      .map((cat) => cat.name);
    if (sensitiveCategories.length == 0) {
      sensitiveCategories.push("None");
    }
    return {
      ...cat,
      description: cat.description?.replace("[[SPI_LIST]]", sensitiveCategories.join(", ")),
    };
  };

  const isDisabled = (
    cat: CategoryDto,
    collected: string[],
    dataMap: DataMapDto,
    disabledCategoryIds?: string[],
  ): boolean => {
    let disabled = Array.isArray(disabledCategoryIds) && disabledCategoryIds.includes(cat.id);
    const sensitiveCategories = _.sortBy(dataMap?.categories, ["picGroupOrder", "picGroupName"])
      .filter((cat) => cat.isSensitiveInCa && collected.includes(cat.id))
      .map((cat) => cat.name);
    if (sensitiveCategories.length == 0 && cat.description?.includes("[[SPI_LIST]]")) {
      disabled = true;
    }
    return disabled;
  };

  if (!allowCustom && (categories.length < 2 || !columns)) {
    return (
      <>
        {categories.map((cat) => (
          <CategoryCheckbox
            category={fillDynamicDescriptionText(cat, checked, dataMap)}
            key={cat.id}
            disabledTooltipFn={disabledTooltipFn}
            disabled={isDisabled(cat, checked, dataMap, disabledCategoryIds)}
            pending={pendingIds.includes(cat.id)}
            checked={checked.includes(cat.id)}
            descriptions={descriptions}
            onChecked={onChecked}
            badge={
              autoselected.includes(cat.key) && checked.includes(cat.id)
                ? "Auto-selected based on survey response"
                : ""
            }
          />
        ))}
      </>
    );
  }

  const splitIndex = Math.ceil(categories.length / 2);
  const left = categories.slice(0, splitIndex);
  const right = categories.slice(splitIndex);

  const deleteCustomFn = async (categoryId: string) => {
    setCustomPicLoading(true);
    await Api.organization.deleteCustomPic(orgId, categoryId);
    await refreshDataMap();
    setCustomPicLoading(false);
  };

  const getDeleteCustomDialogText = (
    categoryId: string,
    collectionGroupId?: string,
  ): string | undefined => {
    if (!allowCustom || !collectionGroupId) {
      return;
    }
    const groupNames = dataMap.collection
      .filter(({ picId, groupId }) => picId == categoryId && collectionGroupId != groupId)
      .map(({ groupId }) => dataMap.collectionGroups[groupId].name);
    if (!groupNames.length) {
      return;
    }
    return `This custom data category is selected for ${groupNames.join(
      ", ",
    )}. Deleting it here will delete it from everywhere else. Are you sure you want to do that?`;
  };

  return (
    <>
      <div className="flex-row">
        <div className="flex-column w-50">
          {left.map((cat) => (
            <CategoryCheckbox
              category={fillDynamicDescriptionText(cat, checked, dataMap)}
              key={cat.id}
              disabledTooltipFn={disabledTooltipFn}
              disabled={Array.isArray(disabledCategoryIds) && disabledCategoryIds.includes(cat.id)}
              pending={pendingIds.includes(cat.id)}
              checked={checked.includes(cat.id)}
              deleteCustomDialog={getDeleteCustomDialogText(cat.id, collectionGroupId)}
              descriptions={descriptions}
              onChecked={onChecked}
              allowCustom={allowCustom}
              deleteCustomFn={deleteCustomFn}
            />
          ))}
        </div>
        <div className="flex-column w-50">
          {right.map((cat) => (
            <CategoryCheckbox
              category={fillDynamicDescriptionText(cat, checked, dataMap)}
              key={cat.id}
              disabledTooltipFn={disabledTooltipFn}
              disabled={Array.isArray(disabledCategoryIds) && disabledCategoryIds.includes(cat.id)}
              pending={pendingIds.includes(cat.id)}
              checked={checked.includes(cat.id)}
              descriptions={descriptions}
              onChecked={onChecked}
              allowCustom={allowCustom}
              deleteCustomFn={deleteCustomFn}
            />
          ))}
        </div>
      </div>
      {allowCustom && (
        <div>
          {adding && (
            <Form
              onSubmit={async (data: { picName: string }) => {
                setCustomPicLoading(true);
                const pic = await Api.organization.createCustomPic(orgId, data.picName);
                setCustomPicLoading(false);
                setAdding(false);
                onChecked(pic.id, true);
              }}
              render={({ handleSubmit, values }) => (
                <form onSubmit={handleSubmit}>
                  <div className="d-flex flex-row align-items-center">
                    <Text
                      multiline={false}
                      variant="outlined"
                      field="picName"
                      required
                      autoFocus
                      label="Add a category"
                      fullWidth={false}
                      disabled={customPicLoading}
                      size="small"
                      className="collection-checkbox-group-new-category"
                    />
                    <Button
                      variant="contained"
                      onClick={() => {
                        setAdding(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <DisableableButton
                      type="submit"
                      color="primary"
                      variant="contained"
                      disabled={Object.values(dataMap?.categories || {})
                        .map((c) => c.name)
                        .includes(values.picName)}
                      disabledTooltip={`A category already exists with the name "${values.picName}."`}
                    >
                      Save
                    </DisableableButton>
                  </div>
                </form>
              )}
            />
          )}
          {!adding && (
            <Button
              variant="text"
              color="secondary"
              onClick={() => {
                setAdding(true);
              }}
            >
              + Add a category
            </Button>
          )}
        </div>
      )}
    </>
  );
};

const CategoryCheckbox = ({
  category: cat,
  disabledTooltipFn,
  deleteCustomFn,
  deleteCustomDialog,
  allowCustom,
  descriptions,
  disabled,
  pending,
  checked,
  onChecked,
  badge,
}: {
  category: CategoryDto;
  disabledTooltipFn?: (checked: boolean) => string;
  deleteCustomFn?: (categoryId: string) => Promise<void>;
  deleteCustomDialog?: string;
  allowCustom?: boolean;
  disabled?: boolean;
  pending?: boolean;
  checked?: boolean;
  badge?: string;
  descriptions?: boolean;
  onChecked: (id: string, isCollected: boolean) => void;
}) => {
  let disabledTooltip: string | undefined;
  if (disabledTooltipFn) {
    disabledTooltip = disabledTooltipFn(checked);
  }
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Check if this PIC is sensitive in any region
  const isSensitiveInAnyRegion = cat.sensitiveRegions?.length > 0;

  const label = (
    <div className="d-flex flex-row">
      <span>
        {cat.name}
        {isSensitiveInAnyRegion && (
          <span className="ml-xs" style={{ color: "#d32f2f", fontWeight: "bold" }}>
            *
          </span>
        )}
        {badge && (
          <Chip label={badge} color="secondary" className="ml-sm collection-checkbox-group-badge" />
        )}
      </span>
      {allowCustom && deleteCustomFn && (
        <>
          <span
            onClick={async (e) => {
              e.preventDefault();
              if (deleteCustomDialog) {
                setOpen(true);
              } else {
                setLoading(true);
                await deleteCustomFn(cat.id);
                setLoading(false);
              }
            }}
          >
            <Close className="text-t1" />
          </span>
          {deleteCustomDialog && (
            <ConfirmDialog
              open={open}
              onClose={() => {
                setOpen(false);
              }}
              onConfirm={async () => {
                setLoading(true);
                await deleteCustomFn(cat.id);
                setLoading(false);
                setOpen(false);
              }}
              confirmTitle="Are you sure?"
              contentText={deleteCustomDialog}
            />
          )}
        </>
      )}
    </div>
  );
  return (
    <DescriptionCheckbox
      key={cat.id}
      label={label}
      description={descriptions && cat.description}
      checked={checked}
      loading={pending || loading}
      onChange={(e, checked) => {
        onChecked(cat.id, checked);
      }}
      disabled={disabled || loading}
      disabledTooltip={disabledTooltip}
      color="primary"
    />
  );
};
